import React, { ReactElement, useCallback, useRef, useEffect, useMemo } from 'react'

import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import styled from 'styled-components'

import i18n from 'live/services/i18n'
import { TemporaryDataType } from '../temporary-data-table/useTemporaryDataState'

interface ErrorCardProps {
    error: LocalErrorsTypes | HierarchyErrorsTypes
    lastClickedError: number
    activeError: string
    setActiveError: (value: string) => void
    setLastClickedError: (value: number) => void
    setExpandedRowKeysByEntity: (entity: keyof ExpandedRowKeys, keys: React.Key[]) => void
    handleExpandAll: () => void
    data: TemporaryDataType
}

export const ErrorCard = ({
    error,
    activeError,
    setActiveError,
    setLastClickedError,
    setExpandedRowKeysByEntity,
    data
}: ErrorCardProps): ReactElement => {
    const lastClickedElementRef = useRef<HTMLElement | null>(null)
    const activeErrorRef = useRef<string>(activeError)
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

    // Memoize error paths already calculated
    const errorPathCacheRef = useRef<Map<number, React.Key[]>>(new Map())

    // Update ref when activeError changes (without causing function recreation)
    useEffect(() => {
        activeErrorRef.current = activeError
    }, [activeError])

    // Limpar cache quando data muda
    useEffect(() => {
        errorPathCacheRef.current.clear()
    }, [data])


    // Compute indices once per data change
    const dataIndices = useMemo(() => {
        const indices = {
            rowIdMap: new Map<string, { item: EntityDto; type: string }>(),
            nodeIdMap: new Map<string, EntityDto>()
        }
        Object.entries(data).forEach(([type, items]) => {
            if (Array.isArray(items)) {
                items.forEach((item: EntityDto) => {
                    indices.rowIdMap.set(String(item.rowId), { item, type })
                    indices.nodeIdMap.set(String(item.nodeId), item)
                })
            }
        })
        return indices
    }, [data])

    const getErrorPathExpandedKeys = useCallback(
        (rowId: number, indices = dataIndices): React.Key[] => {
            // Check cache
            if (errorPathCacheRef.current.has(rowId)) {
                return errorPathCacheRef.current.get(rowId)
            }

            const expandedKeys: React.Key[] = []
            const foundEntry = indices.rowIdMap.get(String(rowId))

            if (!foundEntry) return expandedKeys

            const { item: foundItem } = foundEntry
            expandedKeys.push(String(rowId))

            // Go up the hierarchy using maps (O(1) instead of O(n))
            let currentParentNodeId = String(foundItem.parent)
            let parentItem = currentParentNodeId ? indices.nodeIdMap.get(currentParentNodeId) : null

            while (parentItem) {
                expandedKeys.push(String(parentItem.rowId))
                currentParentNodeId = String(parentItem.parent)
                parentItem = currentParentNodeId ? indices.nodeIdMap.get(currentParentNodeId) : null
            }

            // Store in cache
            errorPathCacheRef.current.set(rowId, expandedKeys)

            return expandedKeys
        },
        [dataIndices]
    )

    const goToEntityError = useCallback(
        (rowId: number, message: string): void => {
            // Remove class from previous element
            if (lastClickedElementRef.current) {
                lastClickedElementRef.current.classList.remove('active-error')
            }

            // Use ref instead of activeError to avoid dependency
            const isToggling = activeErrorRef.current === `${rowId}-${message}`

            if (isToggling) {
                setActiveError('')
                setLastClickedError(null)
                lastClickedElementRef.current = null

                return
            }

            requestAnimationFrame(() => {
                const newClickedElement = document.querySelector<HTMLElement>(`tr[data-row-key='${rowId}']`)

                if (newClickedElement) {
                    newClickedElement.classList.add('active-error')
                    lastClickedElementRef.current = newClickedElement
                    // Scroll without smooth behavior for better performance
                    newClickedElement.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
                }
            })

            // State updates em uma chamada
            setLastClickedError(rowId)
            setActiveError(`${rowId}-${message}`)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [] // No dependencies
    )

    // Debounce to avoid multiple rapid clicks
    const expandAndGoToError = useCallback(
        (rowId: number, message: string): void => {
            // Clear previous timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }

            // Expand all parent entities in the error path
            const errorPathKeys = getErrorPathExpandedKeys(rowId, dataIndices)
            // Map of entity type to keys to expand
            const keysByEntity: Partial<Record<keyof ExpandedRowKeys, React.Key[]>> = {}
            errorPathKeys.forEach(key => {
                const entry = dataIndices.rowIdMap.get(String(key)) as { type: keyof ExpandedRowKeys } | undefined
                if (entry) {
                    const entityType = entry.type
                    if (!keysByEntity[entityType]) {
                        keysByEntity[entityType] = []
                    }
                    keysByEntity[entityType]!.push(String(key))
                }
            })
            // Expand each entity type in the path
            ;(Object.keys(keysByEntity) as Array<keyof ExpandedRowKeys>).forEach(entityType => {
                const keys = keysByEntity[entityType]
                if (keys) {
                    setExpandedRowKeysByEntity(entityType, keys)
                }
            })

            // Wait 200ms before navigating (ensures expandedRowKeys was rendered)
            debounceTimerRef.current = setTimeout(() => {
                goToEntityError(rowId, message)
            }, 200)
        },
        [getErrorPathExpandedKeys, goToEntityError, setExpandedRowKeysByEntity, dataIndices]
    )

    // Clear debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    return (
        <ErrorCardContainer isActiveError={activeError === `${error.rowId}-${error.message}`}>
            <span>{error.message ? i18n(error.message) : `${error.columnName ?? ''} ${i18n('must be resolved')}`}</span>
            {error.rowId && (
                <IconAndEntityNameContainer>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="take-me-there-tooltip">{i18n('Take me there')}</Tooltip>}
                    >
                        <button
                            className="take-me-there-btn"
                            onClick={() => {
                                // handleExpandAll()
                                expandAndGoToError(error.rowId, error.message)
                            }}
                        >
                            <i className="far fa-sign-in" />
                        </button>
                    </OverlayTrigger>
                    <span className="entity-name-span">{`${i18n('Within')} ${i18n(error.entityName)}`}</span>
                </IconAndEntityNameContainer>
            )}
        </ErrorCardContainer>
    )
}

const ErrorCardContainer = styled.div<{ isActiveError: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 1rem;

    padding: 1rem;
    margin-bottom: 12px;

    border-radius: 3px;
    border: 1px solid ${({ isActiveError }) => (isActiveError ? 'var(--red-500)' : 'var(--gray-400)')};

    span {
        font-size: 13px;
        line-height: 15px;
    }
`

const IconAndEntityNameContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    gap: 1rem;

    .take-me-there-btn {
        padding: 0;

        border: none;
        background-color: transparent;

        i {
            transform: rotate(180deg);

            font-size: 16px;
            color: var(--blue-500);

            &:hover {
                opacity: 0.8;
            }
        }
    }

    .entity-name-span {
        font-size: 12px;
        line-height: 16px;

        color: var(--gray-700);
    }
`
